/*
 * Copyright (c) 2020, WSO2 Inc. (http://www.wso2.org) All Rights Reserved.
 *
 * WSO2 Inc. licenses this file to you under the Apache License,
 * Version 2.0 (the "License"); you may not use this file except
 * in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied. See the License for the
 * specific language governing permissions and limitations
 * under the License.
 *
 *
 */

import React from 'react';
import { useSelector } from 'react-redux';
import {makeStyles} from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import {Table, TableHead, TableCell, TableRow} from '@material-ui/core';
import HeadingSection from './commons/HeadingSection'
import Typography from '@material-ui/core/Typography';
import HTTPClient from '../../utils/HTTPClient';
import EnabledIcon from '@material-ui/icons/CheckCircleOutlineOutlined';
import DisabledIcon from '@material-ui/icons/BlockOutlined';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

export default function CarbonApplicationsSideDrawer(props) {
    const globalGroupId = useSelector(state => state.groupId);
    var nodeData = props.nodeData;
    const nodeId = nodeData.nodeId;
    const cappName = nodeData.details.name;
    const isFaulty = nodeData.details.status === 'faulty';
    const [artifacts, setArtifacts] = React.useState([]);
    const classes = useStyles();

    React.useEffect(() => {
        if (!isFaulty) {
            HTTPClient.getCappArtifacts(globalGroupId, nodeId, cappName).then(response => {
                setArtifacts(response.data);
            }).catch(() => {
                setArtifacts([]);
            });
        }
    }, [])

    return (
        <div className={classes.root}>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <HeadingSection name={cappName} nodeId={nodeId}/>
                    <Paper className={classes.paper} elevation={0} square>
                        <CarbonAppsDetailTable nodeData={nodeData.details}/>
                    </Paper>
                    {isFaulty && (
                        <Box pl={2} pr={2} pt={1}>
                            <StackTraceSection groupId={globalGroupId} nodeId={nodeId} cappName={cappName}/>
                        </Box>
                    )}
                    {!isFaulty && (
                        <Box pl={2}>
                            <ArtifactsSection artifacts={artifacts}/>
                        </Box>
                    )}
                </Grid>
            </Grid>
        </div>
    );
}

function CarbonAppsDetailTable(props) {
    const nodeData = props.nodeData;
    const isFaulty = nodeData.status === 'faulty';
    return <Table>
        <TableRow>
            <TableCell>Carbon Application Name</TableCell>
            <TableCell>{nodeData.name}</TableCell>
        </TableRow>
        <TableRow>
            <TableCell>Version</TableCell>
            <TableCell>{nodeData.version}</TableCell>
        </TableRow>
        <TableRow>
            <TableCell>Status</TableCell>
            <TableCell>
                {isFaulty
                    ? <><DisabledIcon style={{color: 'red', verticalAlign: 'middle'}}/> Faulty</>
                    : <><EnabledIcon style={{color: 'green', verticalAlign: 'middle'}}/> Active</>}
            </TableCell>
        </TableRow>
        {isFaulty && nodeData.errorMessage && (
            <TableRow>
                <TableCell>Error Message</TableCell>
                <TableCell style={{color: 'red'}}>{nodeData.errorMessage}</TableCell>
            </TableRow>
        )}
    </Table>
}

function StackTraceSection(props) {
    const { groupId, nodeId, cappName } = props;
    const [stackTrace, setStackTrace] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(false);

    const handleExpand = (event, expanded) => {
        if (expanded && stackTrace === null && !loading) {
            setLoading(true);
            setError(false);
            HTTPClient.getCappFaultInfo(groupId, nodeId, cappName).then(response => {
                setStackTrace(response.data.faultStackTrace || 'No stack trace available.');
                setLoading(false);
            }).catch(() => {
                setError(true);
                setLoading(false);
            });
        }
    };

    return (
        <ExpansionPanel onChange={handleExpand}>
            <ExpansionPanelSummary expandIcon={<ExpandMoreIcon/>}>
                <Typography variant="subtitle1">Stack Trace</Typography>
            </ExpansionPanelSummary>
            <ExpansionPanelDetails>
                {loading && <Typography variant="body2">Loading...</Typography>}
                {error && <Typography variant="body2" style={{color: 'red'}}>Failed to load stack trace.</Typography>}
                {!loading && !error && stackTrace !== null && (
                    <Typography variant="body2" style={{whiteSpace: 'pre-wrap', wordBreak: 'break-all'}}>
                        {stackTrace}
                    </Typography>
                )}
            </ExpansionPanelDetails>
        </ExpansionPanel>
    );
}

function ArtifactsSection(props) {
    const artifacts = props.artifacts;
    return <>
        <Box pl={2}>
            <Typography variant="h6" color="inherit" noWrap>
                Artifacts
            </Typography>
        </Box>
        <Box>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>Artifact Name</TableCell>
                        <TableCell>Artifact Type</TableCell>
                    </TableRow>
                </TableHead>
                {artifacts.map(artifact => <TableRow>
                    <TableCell>{artifact.name}</TableCell>
                    <TableCell>{artifact.type === 'lib' ? 'connector' : artifact.type}</TableCell>
                </TableRow>)}
            </Table>
        </Box>
    </>
}

const useStyles = makeStyles((theme) => ({
    root: {
        flexGrow: 1,
        width: 700,
        overflowX: 'hidden',
    },
    paper: {
        padding: theme.spacing(2),
    },
}));
